import type {
  FeaturedAds,
  HealthCareIdentifier,
  Prospect,
  UserTracking,
} from '@hc-frontend/core-types';
import type { DomainIds } from '@hc-frontend/core-types';
import { getIpFromHeaders } from '@hc-frontend/core-utils-net';
import { userAgent as getUserAgent } from 'next/server';

import {
  fetchMediaAlphaAdsInternal,
  getMember,
  mapQLE,
} from './fetch-internal-media-alpha';

type Props = {
  prospect: Prospect;
  identifier: HealthCareIdentifier;
  domainIdentifiers: Partial<DomainIds>;
  trackingParameters: Partial<UserTracking>;
  apiToken: string;
  placementId: string;
  urlAPI: string;
  smid?: string;
  segmentationFit: string;
  isMedicare?: boolean;
};

export async function fetchMediaAlphaAds({
  prospect,
  identifier,
  domainIdentifiers,
  trackingParameters,
  apiToken,
  placementId,
  urlAPI,
  smid,
  segmentationFit,
  isMedicare = false,
}: Props): Promise<FeaturedAds> {
  const defaultResponse: FeaturedAds = {
    id: '',
    adsSource: 'MediaAlpha',
    searchMethod: 'DEFAULT',
    segmentationFit,
    result: [],
  };

  const headers = await (await import('next/headers')).headers();
  const primary = getMember(prospect.applicant);

  defaultResponse.result = await fetchMediaAlphaAdsInternal(urlAPI, {
    api_token: apiToken,
    placement_id: placementId,
    version: 17,
    ip: getIpFromHeaders(headers) ?? '0.0.0.0',
    ua: getUserAgent({ headers }).ua,
    ua_language: 'en_US',
    url: headers.get('x-forwarded-host') ?? headers.get('host') ?? '',
    sub_1: trackingParameters.subid,
    sub_2: trackingParameters.source,
    sub_3: identifier.sessionId + ':' + domainIdentifiers.sessionId,
    ...(isMedicare ? { cms_smids: smid } : {}),
    data: {
      zip: prospect.location?.zipCode ?? '',
      county: prospect.location?.county ?? '',
      contact: prospect.contactInformation.firstName ?? '',
      email: prospect.contactInformation.email ?? '',
      phone: prospect.contactInformation.phoneNumber ?? '',
      address: prospect.contactInformation.address ?? '',
      household_income: prospect.householdIncome,
      household_size: prospect.householdSize,
      qualifying_life_event: mapQLE(prospect.qualifyingLifeEvent),
      ...(isMedicare
        ? {
            gender: primary?.gender,
            birth_date: primary?.birth_date,
            tobacco: primary?.tobacco,
          }
        : {
            children: prospect.children
              .map(getMember)
              .filter((e) => typeof e !== 'undefined'),
            primary,
            spouse: getMember(prospect.spouse),
          }),
    },
  });

  return defaultResponse;
}
